import type { RefObject } from 'react';
import { useRef, useEffect } from 'react';

interface VoicePulseProps {
  analyserRef: RefObject<AnalyserNode | null>;
  color: 'green' | 'purple';
}

/**
 * Audio-reactive concentric rings around the FAB.
 * Reads frequency data from an AnalyserNode at ~60fps via rAF,
 * driving ring scale + opacity through direct DOM manipulation (no React re-renders).
 */
export function VoicePulse({ analyserRef, color }: VoicePulseProps) {
  const ring1Ref = useRef<HTMLSpanElement>(null);
  const ring2Ref = useRef<HTMLSpanElement>(null);
  const ring3Ref = useRef<HTMLSpanElement>(null);
  const frameRef = useRef(0);
  const smoothedRef = useRef(0);

  useEffect(() => {
    const dataArray = new Uint8Array(64);

    const tick = () => {
      const analyser = analyserRef.current;
      let level = 0;

      if (analyser) {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        level = sum / (dataArray.length * 255); // normalize to 0-1
      }

      // Smooth with fast attack / slow decay for natural feel
      const target = level;
      const current = smoothedRef.current;
      smoothedRef.current = target > current
        ? current * 0.5 + target * 0.5   // fast attack
        : current * 0.85 + target * 0.15; // slow decay

      const s = smoothedRef.current;

      // Scale rings proportionally — inner reacts more, outer reacts most
      const s1 = 1 + s * 0.5;
      const s2 = 1 + s * 0.9;
      const s3 = 1 + s * 1.3;

      // Opacity: base visibility + audio-driven boost
      const o1 = 0.25 + s * 0.35;
      const o2 = 0.18 + s * 0.25;
      const o3 = 0.10 + s * 0.18;

      if (ring1Ref.current) {
        ring1Ref.current.style.transform = `scale(${s1})`;
        ring1Ref.current.style.opacity = `${o1}`;
      }
      if (ring2Ref.current) {
        ring2Ref.current.style.transform = `scale(${s2})`;
        ring2Ref.current.style.opacity = `${o2}`;
      }
      if (ring3Ref.current) {
        ring3Ref.current.style.transform = `scale(${s3})`;
        ring3Ref.current.style.opacity = `${o3}`;
      }

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [analyserRef]);

  const bg = color === 'green' ? 'bg-emerald-400' : 'bg-violet-400';

  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <span ref={ring1Ref} className={`absolute inset-0 rounded-full ${bg} transition-none`} style={{ opacity: 0.25 }} />
      <span ref={ring2Ref} className={`absolute inset-0 rounded-full ${bg} transition-none`} style={{ opacity: 0.18 }} />
      <span ref={ring3Ref} className={`absolute inset-0 rounded-full ${bg} transition-none`} style={{ opacity: 0.10 }} />
    </div>
  );
}
